import { QuestionType, ThumbnailType } from '../../../../bom/constants';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import FeedbackWrapper from '../../../common/FeedbackWrapper';
import Form from 'react-bootstrap/Form';
import LocalImageIcon from '../../../../bom/components/LocalImageIcon';
import PropTypes from 'prop-types';
import SelectWrapper from '../SelectWrapper';
import StepNumber from '../StepNumber';
import { getUniqueOptions } from '../../../../bom/selectors';
import { reloadUniqueOptions } from '../../../../bom/logics';
import styles from './DropdownSingleSelectQuestion.scss';
import { toggleSingleSelectionOption } from '../../../../bom/actions';
import { updatePricingIfNeededForYearOptions } from '../../../../main/logics';

const DropdownSingleSelectQuestion = ({ feedback, question, resetFeedback }) => {
  let { code, questionText, questionType, thumbnailType } = question;

  const selectId = `question-${code}`;
  const labelId = `${selectId}-label`;

  questionText = (questionText || '').trim();
  if (!questionText.endsWith(':')) questionText += ':';

  const dispatch = useDispatch();
  const bomOptions = useSelector((state) => getUniqueOptions(state, code));

  const [selectedOption, setSelectedOption] = useState(() => {
    const selectedBomOption = bomOptions.find((option) => option.isSelected);
    return !!selectedBomOption
      ? {
          bomOption: selectedBomOption,
          isDisabled: selectedBomOption.isSoldOut,
          value: selectedBomOption.codeValue,
        }
      : null;
  });

  useEffect(() => {
    const selectedBomOption = bomOptions.find((option) => option.isSelected);
    setSelectedOption(
      !!selectedBomOption
        ? {
            bomOption: selectedBomOption,
            isDisabled: selectedBomOption.isSoldOut,
            value: selectedBomOption.codeValue,
          }
        : null
    );

    if (!!selectedBomOption && !!feedback[selectedBomOption.code]) {
      resetFeedback(selectedBomOption.code);
    }
  }, [bomOptions, feedback, resetFeedback]);

  const options = bomOptions.map((bomOption) => ({
    bomOption,
    isDisabled: bomOption.isSoldOut,
    value: bomOption.codeValue,
  }));

  const handleFormatOptionLabel = ({ bomOption }) => {
    const { codeValue, isOutOfStock, isSoldOut, optionText, outOfStockMessage } = bomOption;
    return (
      <div className={styles['option']}>
        {thumbnailType === ThumbnailType.LOCAL_IMAGE && <LocalImageIcon imageName={codeValue} size="sm" />}
        <div className={styles['option-label']}>
          {optionText}
          {isOutOfStock && (
            <span className={styles['out-of-stock']}>
              {!!outOfStockMessage ? outOfStockMessage : 'Temporarily out of stock.'}
            </span>
          )}
          {isSoldOut && <span className={styles['sold-out']}>Sold out</span>}
        </div>
      </div>
    );
  };

  const handleChange = (option) => {
    if (!option.bomOption.isSelected) {
      setSelectedOption(option);

      const { bomOption } = option;
      const { code, codeValue } = bomOption;
      dispatch(toggleSingleSelectionOption(code, codeValue));
      if (questionType !== QuestionType.GENERAL) {
        dispatch(reloadUniqueOptions(question));
      }
      dispatch(updatePricingIfNeededForYearOptions());

      resetFeedback(code);
    }
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label id={labelId}>
        <StepNumber step={code} />
        {questionText}
      </Form.Label>
      <SelectWrapper
        aria-labelledby={labelId}
        className={styles['single-select-question-dropdown']}
        formatOptionLabel={handleFormatOptionLabel}
        id={selectId}
        isInvalid={!!feedback[code]}
        isSearchable={false}
        options={options}
        placeholder="Select option"
        value={selectedOption}
        onChange={handleChange}
      />
      {!!feedback[code] && <FeedbackWrapper>{feedback[code]}</FeedbackWrapper>}
    </Form.Group>
  );
};

DropdownSingleSelectQuestion.propTypes = {
  feedback: PropTypes.objectOf(PropTypes.string).isRequired,
  question: PropTypes.shape({
    code: PropTypes.string.isRequired,
    isRealParentChildQuestion: PropTypes.bool.isRequired,
    itemId: PropTypes.string.isRequired,
    mandatoryFlag: PropTypes.string.isRequired,
    needToReloadUniqueOptions: PropTypes.bool.isRequired,
    questionId: PropTypes.number.isRequired,
    questionLabel: PropTypes.string.isRequired,
    questionText: PropTypes.string.isRequired,
    questionType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
    representationType: PropTypes.oneOf([0, 1]).isRequired,
    sampleQuestionText: PropTypes.string,
    selectionType: PropTypes.oneOf([0, 1]).isRequired,
    sequence: PropTypes.number.isRequired,
    thumbnailType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
  }).isRequired,
  resetFeedback: PropTypes.func.isRequired,
};

export default DropdownSingleSelectQuestion;
